// src/components/Modals/PhoneSelectionModal.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatCUIL } from '../../utils/documentFormatter';
import { PhoneField } from '../ui';
import { getInitials } from '../../utils/avatar';

const PhoneSelectionModal = ({ client, onConfirm, onClose, loading = false }) => {
  const [phonePayload, setPhonePayload] = useState({
    e164: null,
    countryIso2: null,
    national: null,
    isPossible: false,
    isValid: false,
    type: 'UNKNOWN',
    rawInput: ''
  });
  const [hasBlurred, setHasBlurred] = useState(false);
  const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);

  const allowPossibleButInvalid = process.env.REACT_APP_PHONE_ALLOW_POSSIBLE_WARNING === 'true';

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  const handlePhoneChange = (payload) => {
    setPhonePayload(payload);
  };

  const handlePhoneBlur = (payload) => {
    setHasBlurred(true);
    if (payload) {
      setPhonePayload(payload);
    }
  };

  const validationError = useMemo(() => {
    const strictValidationEnabled = hasBlurred || hasSubmitAttempt;
    if (!strictValidationEnabled) return '';

    const hasInput = Boolean(phonePayload.rawInput?.trim() || phonePayload.e164);

    if (!hasInput) {
      return 'Ingresa un número de teléfono';
    }

    if (!phonePayload.e164) {
      return 'No pudimos interpretar el número. Revisa país y formato.';
    }

    if (!phonePayload.isValid) {
      if (allowPossibleButInvalid && phonePayload.isPossible) {
        return '';
      }
      return 'El número no es válido para el país seleccionado';
    }

    return '';
  }, [phonePayload, hasBlurred, hasSubmitAttempt, allowPossibleButInvalid]);

  const showPossibleWarning = useMemo(() => {
    const strictValidationEnabled = hasBlurred || hasSubmitAttempt;
    return (
      strictValidationEnabled &&
      allowPossibleButInvalid &&
      phonePayload.e164 &&
      phonePayload.isPossible &&
      !phonePayload.isValid
    );
  }, [phonePayload, hasBlurred, hasSubmitAttempt, allowPossibleButInvalid]);

  const handleConfirm = () => {
    setHasSubmitAttempt(true);
    if (validationError) return;

    onConfirm({
      ...client,
      phone: phonePayload.e164
    });
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <>
      <div className="modal-backdrop fade show rg-modal-backdrop rg-modal-backdrop--seller"></div>
      <div className="modal fade text-left w-100 show rg-modal rg-modal--seller rg-modal--phone-input" tabIndex="-1" style={{ display: 'block' }} aria-modal="true" role="dialog">
        <div className="modal-dialog modal-md modal-dialog-centered rg-modal__dialog rg-modal__dialog--md rg-modal__dialog--tall">
          <div className="modal-content rg-modal__content">
            <div className="modal-body rg-modal__body">
              <p className="text-center fw-bold fs-5 text-primary rg-modal__title rg-modal__title--center">
                Número de teléfono
              </p>
              <p className="rg-modal__body-text">
                Este número se utilizará para continuar validaciones y notificaciones del proceso.
              </p>
              
              {/* Información del cliente */}
              <div className="card mb-3 rg-modal__profile-card rg-modal__profile-card--compact">
                <div className="card-body p-3">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm me-2 rg-modal__avatar rg-modal__avatar--sm">
                      <span className="rg-modal__avatar-initials" aria-hidden>{getInitials(client.name, 'CL')}</span>
                    </div>
                    <div>
                      <h6 className="mb-0 text-primary fw-bold rg-modal__profile-name">{client.name}</h6>
                      <small className="text-muted rg-modal__profile-meta">CUIL: {formatCUIL(client.cuit)}</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* Campo de teléfono global (país + formato + validación E.164) */}
              <div className="form-group my-2 rg-modal__field">
                <PhoneField
                  id="phoneInput"
                  name="phone"
                  label=""
                  placeholder="Ej: 11 2345 6789"
                  defaultCountry="AR"
                  required
                  disabled={loading}
                  error={validationError}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  autoFocus
                />

                {showPossibleWarning && (
                  <small className="form-text text-warning d-block mt-1">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    El número parece posible, pero no completamente válido para el país.
                  </small>
                )}
              </div>
            </div>
            
            <div className="modal-footer rg-modal__actions">
              <button 
                type="button" 
                className="btn btn-outline-secondary rg-modal__action-btn" 
                onClick={onClose}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn btn-primary rg-modal__action-btn" 
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Procesando...
                  </>
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

PhoneSelectionModal.propTypes = {
  client: PropTypes.shape({
    name: PropTypes.string.isRequired,
    cuit: PropTypes.string.isRequired,
    gender: PropTypes.string
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default PhoneSelectionModal;
